import './style.scss';

import {
  autoUpdate,
  FloatingFocusManager,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import cn from 'classnames';
import { motion } from 'framer-motion';
import isNil from 'lodash/isNil';
import React, { HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { useThemeColors } from '@/shared/theme';
import { SmartFloatingPortal as FloatingPortal } from '@/shared/ui/SmartFloatingPortal';

import {
  fieldLabelFocused,
  fieldLabelNotFocusedFilled,
  fieldLabelNotFocusedNotFilled,
} from 'shared/consts/fields';

import { FieldHints } from '../../components';

import { ArrowDropDown } from 'assets/icons';

interface IBaseSelectFieldProps<V> extends HTMLAttributes<HTMLInputElement> {
  selectedItem?: V;
  onSelectItem: (item: V | undefined) => void;
  items: V[];
  renderItem: (item: V, searchTerm?: string) => React.ReactNode;
  selectedItemText?: string;
  renderSelectedItem?: (item: V) => React.ReactNode;
  comment?: React.ReactNode;
  errorMessage?: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  /** Optional filter function to enable search. When provided, input becomes editable. */
  filterFunction?: (item: V, searchTerm: string) => boolean;
}

function BaseSelectField<V>({
  comment,
  errorMessage,
  placeholder,
  label,
  disabled = false,
  onClick: _onClick,
  className,
  id,
  items,
  selectedItem,
  selectedItemText,
  renderSelectedItem,
  onSelectItem,
  renderItem,
  filterFunction,
  ...rest
}: IBaseSelectFieldProps<V>) {
  const palette = useThemeColors();
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Filter items when filterFunction is provided
  const filteredItems = useMemo(() => {
    if (!filterFunction || !searchTerm.trim()) return items;
    return items.filter(item => filterFunction(item, searchTerm));
  }, [items, filterFunction, searchTerm]);

  // Determine if search is enabled
  const isSearchable = !!filterFunction;

  // Handle dropdown open/close - clear search when closing
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (disabled) return;
      setIsOpened(open);
      if (!open) {
        setSearchTerm('');
        setActiveIndex(null);
      }
    },
    [disabled],
  );

  const { refs, context, floatingStyles } = useFloating({
    placement: 'bottom-start',
    open: disabled ? false : isOpened,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const listRef = useRef<Array<HTMLElement | null>>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpened && isSearchable) {
      // Use setTimeout to ensure focus happens after floating-ui initialization
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpened, isSearchable]);

  const click = useClick(context, { event: 'mousedown' });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    dismiss,
    role,
    listNav,
    click,
  ]);

  const handleSelect = useCallback(
    (item: V, index: number) => {
      setSelectedIndex(index);
      onSelectItem(item);
      setIsOpened(false);
      setSearchTerm('');
    },
    [onSelectItem],
  );

  const value = useMemo(() => {
    // When searchable and dropdown is open, show search term
    if (isSearchable && isOpened) {
      return searchTerm;
    }
    // Otherwise show selected item text
    return !isNil(selectedItem)
      ? renderSelectedItem
        ? ' '
        : selectedItemText
          ? selectedItemText
          : ''
      : '';
  }, [selectedItem, selectedItemText, renderSelectedItem, isSearchable, isOpened, searchTerm]);

  const inputId = useMemo(() => {
    return id ?? `mm-select-${uuid()}`;
  }, [id]);

  const lineStyles = useMemo(() => {
    let borderColor = 'rgba(127, 145, 187, 0.2)';

    if (errorMessage !== undefined) {
      borderColor = palette.red_1;
    } else if (isOpened) {
      borderColor = palette.primary;
    }

    return { borderBottom: `2px solid ${borderColor}` };
  }, [isOpened, errorMessage, palette]);

  const labelStyles = useMemo(() => {
    // For label positioning, consider selected item (not search term)
    const hasSelectedItem = !isNil(selectedItem) && selectedItemText !== '';
    const fieldFilled = hasSelectedItem || (isSearchable && isOpened && searchTerm !== '');
    const fieldFocused = isOpened;

    if (fieldFocused) return fieldLabelFocused(!!errorMessage);

    if (!fieldFocused && fieldFilled) return fieldLabelNotFocusedFilled(!!errorMessage);

    return fieldLabelNotFocusedNotFilled;
  }, [selectedItem, selectedItemText, isOpened, errorMessage, isSearchable, searchTerm]);

  return (
    <>
      <div
        className={cn({ [className ?? '']: className }, 'mm-base-select-field', {
          '_non-label': !label || label === '',
        })}
        {...rest}
      >
        <div
          className={cn('mm-base-select-field__container', {
            _disabled: disabled,
          })}
          tabIndex={0}
          ref={refs.setReference}
          aria-labelledby="select-label"
          aria-autocomplete="none"
          {...getReferenceProps()}
        >
          <div className="mm-base-select-field__inner">
            {!isNil(selectedItem) && renderSelectedItem && (
              <div className="select-node-left">{renderSelectedItem(selectedItem)}</div>
            )}
            <div className="mm-base-select-field__inner__input-holder">
              <input
                ref={inputRef}
                className={cn('mm-base-select-field__inner__input', { _focused: isOpened })}
                value={!isNil(selectedItem) && renderSelectedItem && !isOpened ? ' ' : value}
                id={inputId}
                autoComplete="off"
                type={'text'}
                placeholder={isSearchable && isOpened ? 'Search...' : placeholder}
                readOnly={!isSearchable}
                disabled={disabled}
                onChange={
                  isSearchable
                    ? e => {
                        setSearchTerm(e.target.value);
                        // Open dropdown when typing
                        if (!isOpened) setIsOpened(true);
                      }
                    : undefined
                }
                onMouseDown={
                  isSearchable
                    ? e => {
                        // Prevent dropdown toggle when clicking input in search mode
                        // useClick hook uses mousedown event
                        e.stopPropagation();
                        if (!isOpened) setIsOpened(true);
                      }
                    : undefined
                }
              />
            </div>
            {label ? (
              <motion.label
                initial={fieldLabelNotFocusedNotFilled}
                animate={labelStyles}
                className={cn('mm-base-select-field__inner__label', { _disabled: !!disabled })}
                htmlFor={inputId}
              >
                {label}
              </motion.label>
            ) : null}
            <button className={cn('arrow-angle-down', { _opened: isOpened })}>
              <ArrowDropDown />
            </button>
          </div>
          <motion.div
            animate={lineStyles}
            transition={{ ease: 'easeInOut', duration: 0.4 }}
            className={'mm-base-select-field__line'}
          />
        </div>
        <FieldHints comment={comment} errorMessage={errorMessage} />
      </div>
      {isOpened && (
        <FloatingPortal>
          <FloatingFocusManager
            context={context}
            modal={false}
            initialFocus={isSearchable ? -1 : 0}
          >
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className="mm-base-select-field__dropdown scrollable"
              {...getFloatingProps()}
            >
              {filteredItems.map((item, i) => (
                <div
                  className="mm-base-select-field__dropdown__item"
                  key={i}
                  ref={node => {
                    listRef.current[i] = node;
                  }}
                  role="option"
                  tabIndex={i === activeIndex ? 0 : -1}
                  aria-selected={i === selectedIndex && i === activeIndex}
                  style={{
                    background: i === activeIndex ? palette.primary_light : '',
                  }}
                  {...getItemProps({
                    onClick() {
                      handleSelect(item, i);
                    },
                    onKeyDown(event) {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleSelect(item, i);
                      }

                      if (event.key === ' ' && !isSearchable) {
                        // Don't handle space as select when searchable (allow typing)
                        event.preventDefault();
                        handleSelect(item, i);
                      }
                    },
                  })}
                >
                  {renderItem(item, isSearchable ? searchTerm : undefined)}
                </div>
              ))}
              {filteredItems.length === 0 && (
                <div className="dropdown__not-found">Not found...</div>
              )}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}

export { BaseSelectField };
